import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/taskService';
import { jobService } from '../services/jobService';
import { userService } from '../services/userService';
import { SideNav } from './SideNav';
import { 
  PageLayout, 
  Container, 
  Grid, 
  Card, 
  H2, 
  H6, 
  Body2
} from './ui';

// Circular Progress Component
function CircularProgress({ percentage, size = 120, strokeWidth = 8 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
        {/* Gradient definition */}
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
      </svg>
      {/* Percentage text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white">{percentage}%</span>
        <span className="text-xs text-gray-300">Complete</span>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, sideNavLocked, setSideNavLocked } = useAuth();
  const navigate = useNavigate();
  const [openTasks, setOpenTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [notification, setNotification] = useState(null);
  const [completedTaskIds, setCompletedTaskIds] = useState(new Set());
  const [jobStats, setJobStats] = useState({ wishlist: 0, applied: 0, interview: 0, offer: 0 });
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [loadingCompleteness, setLoadingCompleteness] = useState(true);





  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Extract first name from user.name as it was entered during signup
  const getFirstName = () => {
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    // Try firstName field as backup
    if (user?.firstName) {
      return user.firstName;
    }
    return '';
  };

  // Fetch open tasks
  useEffect(() => {
    const fetchOpenTasks = async () => {
      try {
        setLoadingTasks(true);
        const tasks = await taskService.getAllTasks();
        // Filter for incomplete tasks and sort by due date
        const incompleteTasks = tasks
          .filter(task => !task.completed)
          .sort((a, b) => {
            // Sort by due date (null dates go to end)
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return new Date(a.dueDate) - new Date(b.dueDate);
          });
        setOpenTasks(incompleteTasks);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchOpenTasks();
  }, []);

  // Fetch job applications for stats
  useEffect(() => {
    const fetchJobStats = async () => {
      try {
        setLoadingJobs(true);
        const jobs = await jobService.getAllJobs();
        
        // Count jobs by stage (excluding rejected)
        const stats = {
          wishlist: jobs.filter(job => job.stage === 'wishlist').length,
          applied: jobs.filter(job => job.stage === 'applied').length,
          interview: jobs.filter(job => job.stage === 'interview').length,
          offer: jobs.filter(job => job.stage === 'offer').length
        };
        
        setJobStats(stats);
      } catch (error) {
        console.error('Error fetching job stats:', error);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobStats();
  }, []);

  // Fetch profile completeness
  useEffect(() => {
    const fetchProfileCompleteness = async () => {
      try {
        setLoadingCompleteness(true);
        const data = await userService.getProfileCompleteness();
        setProfileCompleteness(data);
      } catch (error) {
        console.error('Error fetching profile completeness:', error);
      } finally {
        setLoadingCompleteness(false);
      }
    };

    fetchProfileCompleteness();
  }, []);

  // Format due date for display
  const formatDueDate = (dueDate) => {
    if (!dueDate) return 'No due date';
    
    const date = new Date(dueDate);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    
    return date.toLocaleDateString();
  };

  // Handle task completion toggle
  const handleTaskComplete = async (taskId) => {
    try {
      // Mark task as completed in backend
      await taskService.toggleTaskComplete(taskId, true);
      
      // Add to completed tasks (for strikethrough styling)
      setCompletedTaskIds(prev => new Set([...prev, taskId]));
      
      // Show notification with undo option
      setNotification({
        taskId,
        message: 'Task completed',
        timer: setTimeout(() => {
          // Auto-dismiss after 10 seconds - remove task from view
          setOpenTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
          setCompletedTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
          setNotification(null);
        }, 10000)
      });
      
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  // Handle undo task completion
  const handleUndoComplete = async () => {
    if (!notification) return;
    
    try {
      // Mark task as incomplete in backend
      await taskService.toggleTaskComplete(notification.taskId, false);
      
      // Remove from completed tasks
      setCompletedTaskIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.taskId);
        return newSet;
      });
      
      // Clear notification and timer
      clearTimeout(notification.timer);
      setNotification(null);
      
    } catch (error) {
      console.error('Error undoing task completion:', error);
    }
  };

  // Handle notification close
  const handleCloseNotification = () => {
    if (!notification) return;
    
    // Clear timer and notification
    clearTimeout(notification.timer);
    
    // Remove completed task from view
    setOpenTasks(prevTasks => prevTasks.filter(task => task._id !== notification.taskId));
    setCompletedTaskIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(notification.taskId);
      return newSet;
    });
    
    setNotification(null);
  };



  return (
    <>
      <SideNav onLockChange={setSideNavLocked} />
      <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          <Container maxWidth="lg" className="py-8">
            {/* Notification */}
            {notification && (
              <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top duration-300">
                <div className="bg-white bg-opacity-50 border-[0.4px] border-[#E0E0E0] rounded-lg px-4 py-3 flex items-center gap-4 shadow-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-gray-800">{notification.message}</span>
                  </div>
                  
                  <button
                    onClick={handleUndoComplete}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                  >
                    Undo
                  </button>
                  
                  <button
                    onClick={handleCloseNotification}
                    className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <H2 className="mb-8">{getTimeBasedGreeting()}{getFirstName() ? `, ${getFirstName()}` : ''}</H2>

            {/* Elena Starter Checklist */}
            <Card className="mb-6 bg-opacity-20 border-[0.4px] border-[#E0E0E0]">
              <div className="flex items-start gap-6">
                {/* Circular Progress */}
                <div className="flex-shrink-0">
                  {loadingCompleteness ? (
                    <div className="w-32 h-32 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
                  ) : (
                    <CircularProgress 
                      percentage={profileCompleteness?.overall || 0}
                      size={128}
                      strokeWidth={8}
                    />
                  )}
                </div>

                {/* Checklist Content */}
                <div className="flex-1 min-w-0">
                  <H6 className="mb-3 flex items-center gap-2">
                    🚀 Starter Checklist
                    <span className="text-sm font-normal text-gray-300">
                      {loadingCompleteness ? '...' : `${profileCompleteness?.stats?.completedSections || 0}/${profileCompleteness?.stats?.totalSections || 13} completed`}
                    </span>
                  </H6>
                  
                  {loadingCompleteness ? (
                    <Body2>Loading your progress...</Body2>
                  ) : (
                    <>
                      <Body2 className="mb-4">
                        Complete these steps to get the most out of the platform and accelerate your job search.
                      </Body2>
                      
                      {profileCompleteness?.suggestions?.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3">
                          {profileCompleteness.suggestions.slice(0, 3).map((suggestion, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-lg border border-gray-200 border-opacity-20"
                            >
                              <span className="text-lg flex-shrink-0">{suggestion.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-white">
                                  {suggestion.title}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-green-400">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">Great job! Your profile is complete.</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
            
            {/* Main Row: 2 cards with height 540px, first card 2/3 width */}
            <Grid cols={1} gap={6} className="md:grid-cols-3">
              <div 
                onClick={() => navigate('/job-tracker')}
                className="h-[400px] md:h-[540px] md:col-span-2 cursor-pointer transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
              >
                <Card className="h-full bg-opacity-20 border-[0.4px] border-[#E0E0E0] hover:bg-opacity-30 transition-all duration-200">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2">
                      <H6>Job Applications Overview</H6>
                      <svg className="w-4 h-4 text-gray-400 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-1M14 6h2a2 2 0 012 2v10a2 2 0 01-2 2h-2m-4-8l4-4m0 0l4 4m-4-4v12" />
                      </svg>
                    </div>
                    
                    {/* Total Applications - Top Right */}
                    <div className="text-right">
                      <Body2 className="text-gray-300 text-sm">
                        Total Active: <span className="text-white font-semibold">
                          {Object.values(jobStats).reduce((sum, count) => sum + count, 0)}
                        </span>
                      </Body2>
                      <Body2 className="text-gray-400 text-xs mt-1">Click to view pipeline</Body2>
                    </div>
                  </div>
                
                {loadingJobs ? (
                  <Body2 className="text-center">Loading job data...</Body2>
                ) : (
                  <div className="h-[calc(100%-5rem)]">
                    {/* Vertical Bar Chart */}
                    <div className="flex items-end justify-between h-[300px] md:h-[400px] mb-6 px-4">
                      {[
                        { stage: 'wishlist', label: 'Wishlist', count: jobStats.wishlist, color: '#6B7280' },
                        { stage: 'applied', label: 'Applied', count: jobStats.applied, color: '#3B82F6' },
                        { stage: 'interview', label: 'Interview', count: jobStats.interview, color: '#F59E0B' },
                        { stage: 'offer', label: 'Offer', count: jobStats.offer, color: '#10B981' }
                      ].map((item) => {
                        const maxCount = Math.max(...Object.values(jobStats), 1); // Ensure at least 1 to avoid division by 0
                        const heightPixels = maxCount > 0 ? Math.max((item.count / maxCount) * 250, 20) : 20; // Minimum 20px height
                        
                        return (
                          <div
                            key={item.stage}
                            className="flex flex-col items-center group transition-all duration-200"
                            title={`${item.count} ${item.label} applications`}
                          >
                            {/* Bar Container */}
                            <div className="relative flex flex-col justify-end" style={{ height: '250px' }}>
                              {/* Background Bar */}
                              <div className="w-12 md:w-16 h-full bg-white bg-opacity-10 rounded-t absolute bottom-0" />
                              
                              {/* Colored Bar */}
                              <div 
                                className={`w-12 md:w-16 rounded-t transition-all duration-700 ease-out relative z-10`}
                                style={{ 
                                  height: `${heightPixels}px`,
                                  backgroundColor: item.color
                                }}
                              />
                            </div>
                            
                            {/* Count */}
                            <div className="mt-3 text-lg font-bold text-white">
                              {item.count}
                            </div>
                            
                            {/* Label */}
                            <div className="text-xs text-gray-300 text-center mt-1">
                              {item.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </Card>
              </div>

              <Card className="h-[400px] md:h-[540px] bg-opacity-20 border-[0.4px] border-[#E0E0E0] overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                  <H6>Open Tasks</H6>
                  <button
                    onClick={() => navigate('/tasks')}
                    className="text-xs text-purple-300 hover:text-purple-200 transition-colors duration-200"
                  >
                    View all tasks
                  </button>
                </div>
                
                {loadingTasks ? (
                  <Body2 className="text-center">Loading tasks...</Body2>
                ) : openTasks.length === 0 ? (
                  <Body2 className="text-center text-gray-500">No open tasks</Body2>
                ) : (
                  <div className="overflow-y-auto h-[calc(100%-3rem)]">
                    {openTasks.map((task, index) => {
                      const isCompleted = completedTaskIds.has(task._id);
                      
                      return (
                        <div 
                          key={task._id} 
                          className={`p-3 mb-3 bg-white bg-opacity-10 rounded border border-gray-200 border-opacity-20 ${index >= 8 ? 'hidden' : ''} flex items-start justify-between ${isCompleted ? 'opacity-60' : ''}`}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <div className={`text-sm font-medium text-white mb-1 truncate ${isCompleted ? 'line-through' : ''}`}>
                              {task.title}
                            </div>
                            {task.jobId && (
                              <div className={`text-xs text-gray-300 mb-1 truncate ${isCompleted ? 'line-through' : ''}`}>
                                {task.jobId.jobTitle} at {task.jobId.company}
                              </div>
                            )}
                            <div className={`text-xs ${
                              task.dueDate && new Date(task.dueDate) < new Date() 
                                ? 'text-red-300' 
                                : 'text-gray-400'
                            } ${isCompleted ? 'line-through' : ''}`}>
                              {task.dueDate ? (
                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                                  task.dueDate && new Date(task.dueDate) < new Date()
                                    ? 'bg-red-500 bg-opacity-20 text-red-300 border border-red-500 border-opacity-30'
                                    : 'bg-purple-500 bg-opacity-20 text-purple-200 border border-purple-500 border-opacity-30'
                                }`}>
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                  {formatDueDate(task.dueDate)}
                                </span>
                              ) : (
                                <span className="text-gray-500">No due date</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Completion checkbox */}
                          {!isCompleted && (
                            <button
                              onClick={() => handleTaskComplete(task._id)}
                              className="flex-shrink-0 w-5 h-5 border border-gray-300 border-opacity-50 rounded bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center group"
                              title="Mark as completed"
                            >
                              <svg 
                                className="w-3 h-3 text-white opacity-0 group-hover:opacity-70 transition-opacity duration-200" 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })}
                    
                    {openTasks.length > 8 && (
                      <div className="text-xs text-gray-400 text-center mt-2">
                        +{openTasks.length - 8} more tasks
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </Grid>


          </Container>
        </PageLayout>
      </div>
    </>
  );
}

export default Dashboard; 